// Vocabulary + preflight validation for the brandOverlay processing step.
//
// Everything here mirrors what the pipeline enforces, so a config saved from
// this form doesn't come back as a 300 FAILED row:
//   - positions: brand_common/constants.py ALL_POSITIONS (9 grid + 2 banner)
//   - per-brand fields: brandOverlay_eventValidation.py _validate_brand_entry
//   - placements: brandOverlay_eventValidation.py _index_brand_placements
// Stage 1 is still the authority; these checks just surface the same rules
// while someone is editing rather than after an asset fails.

/** Image groups brandOverlay can brand — the ImageOrder token prefixes it
 *  understands (brandOverlay_eventValidation.py). */
export const BRAND_GROUPS = ["exterior", "featured", "manual", "pano"] as const;

/** The 9-cell grid. Stage 2 renders these today. */
export const BRAND_GRID_POSITIONS = [
  "top-left", "top-center", "top-right",
  "middle-left", "middle-center", "middle-right",
  "bottom-left", "bottom-center", "bottom-right",
] as const;

/** Banner bands. Accepted by Stage 1, but Stage 2 currently treats them as
 *  no-op placeholders until banner geometry is specced. */
export const BRAND_BANNER_POSITIONS = ["header", "footer"] as const;

export const BRAND_POSITIONS: string[] = [
  ...BRAND_GRID_POSITIONS,
  ...BRAND_BANNER_POSITIONS,
];

export interface BrandPlacement {
  group: string;
  image_tag: string;
  position: string;
}

export interface BrandEntry {
  s3_path: string;
  logo_pct: number | string;
  padding_pct: number | string;
  alt_color_allowed: boolean;
  alt_color_value: string;
  placements: BrandPlacement[];
}

/** A new brand row: valid percentages, everything else for the operator to fill
 *  in. Percentages are seeded as strings so a saved config has one shape (the
 *  form's number inputs also produce strings) and no float ever reaches Dynamo,
 *  which only takes Decimal. `_parse_pct_field` accepts either. */
export const blankBrand = (): BrandEntry => ({
  s3_path: "",
  logo_pct: "25",
  padding_pct: "5",
  alt_color_allowed: false,
  alt_color_value: "#FFFFFF",
  placements: [],
});

/** Seeded when the step is switched on. One brand row, because Stage 1 rejects
 *  an empty `branding_images` list outright. */
export const brandOverlayDefaults = {
  branding_images: [blankBrand()],
};

export interface PlacementErrors {
  group?: string;
  image_tag?: string;
  position?: string;
}

export interface BrandErrors {
  s3_path?: string;
  logo_pct?: string;
  padding_pct?: string;
  alt_color_value?: string;
  placements?: string;
  placementErrors: Record<number, PlacementErrors>;
}

/** Percentages ride to Dynamo as strings or numbers; both are parsed the same
 *  way by brand_common/parsing.py `_parse_pct_field`. */
const parsePct = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const validateS3Path = (value: unknown): string | undefined => {
  if (typeof value !== "string" || !value.trim()) return "Required";
  if (!value.startsWith("s3://")) return "Must start with s3://";
  const [bucket, ...rest] = value.slice("s3://".length).split("/");
  const key = rest.join("/");
  if (!bucket || !key) return "Needs a bucket and a key (s3://bucket/logo.png)";
  return undefined;
};

const validateHexColor = (value: unknown): string | undefined => {
  if (typeof value !== "string") return "Must be #RRGGBB hex";
  return /^#?[0-9a-fA-F]{6}$/.test(value.trim()) ? undefined : "Must be #RRGGBB hex";
};

/** Preflight one brand. Returns per-field messages; an entry with no messages
 *  and an empty `placementErrors` is one Stage 1 will accept. */
export function validateBrand(brand: BrandEntry | undefined): BrandErrors {
  const errors: BrandErrors = { placementErrors: {} };
  if (!brand) return errors;

  errors.s3_path = validateS3Path(brand.s3_path);

  const logoPct = parsePct(brand.logo_pct);
  if (logoPct === null) {
    errors.logo_pct = "Must be a number";
  } else if (logoPct <= 0 || logoPct > 100) {
    errors.logo_pct = "Must be greater than 0 and at most 100";
  }

  const paddingPct = parsePct(brand.padding_pct);
  if (paddingPct === null) {
    errors.padding_pct = "Must be a number";
  } else if (paddingPct < 0) {
    errors.padding_pct = "Cannot be negative";
  } else if (paddingPct >= 50) {
    // Padding applies per side, so 50% each side leaves no room in the cell.
    errors.padding_pct = "Must be under 50 (padding is per side)";
  }

  if (brand.alt_color_allowed) {
    errors.alt_color_value = validateHexColor(brand.alt_color_value);
  }

  const placements = Array.isArray(brand.placements) ? brand.placements : [];
  if (placements.length === 0) {
    errors.placements = "Add at least one placement — a brand with none is never applied";
  }

  const seen = new Set<string>();
  placements.forEach((placement, idx) => {
    const rowErrors: PlacementErrors = {};

    if (!placement?.group) rowErrors.group = "Required";
    if (!placement?.image_tag) rowErrors.image_tag = "Required";

    if (!placement?.position) {
      rowErrors.position = "Required";
    } else if (!BRAND_POSITIONS.includes(placement.position)) {
      rowErrors.position = "Not a known position";
    }

    if (placement?.group && placement?.image_tag) {
      const key = `${placement.group} ${placement.image_tag}`;
      if (seen.has(key)) {
        rowErrors.image_tag = "Duplicate group + tag for this brand";
      }
      seen.add(key);
    }

    if (Object.keys(rowErrors).length > 0) errors.placementErrors[idx] = rowErrors;
  });

  return errors;
}

/** True when this brand has nothing Stage 1 would reject. */
export function isBrandValid(errors: BrandErrors): boolean {
  const { placementErrors, ...fields } = errors;
  return (
    Object.values(fields).every((message) => !message) &&
    Object.keys(placementErrors).length === 0
  );
}

/** Count of brands with at least one problem, for the form's summary line. */
export function countInvalidBrands(brands: BrandEntry[] | undefined): number {
  if (!Array.isArray(brands)) return 0;
  return brands.filter((brand) => !isBrandValid(validateBrand(brand))).length;
}
